import { FC, useMemo, useState } from 'react';
import { TJoint, TJointInstallationStep } from '../../../lib/types';
import { Button, Form, Input, Label } from '@bphxd/ds-core-react';
import { FormGroup } from 'reactstrap';
import {
  GET_JOINT_DETAILS,
  GET_JOINT_INSTALLATION_STEP_DETAILS,
  SIGN_JOINT_STEP,
  UPDATE_JOINT,
  UPDATE_JOINT_STEP,
} from '../../../graph-queries/tar/joint';
import {useMutation, useQuery } from '@apollo/client';
import classes from './JointSigningForm.module.scss';
import { Camera24, CircleCheck24, CircleCheck64 } from '@bphxd/ds-core-react/lib/icons';
import { extractAndFormatName } from '../../organisms/AspectsTable/util';
import { formatDate } from 'date-fns';
import { useMediaUploadReducer } from '../../organisms/MediaUpload/MediaUpload.reducer';
import { MediaUploadRow } from '../../organisms/MediaUpload/MediaUpload';
import { GENERATE_SIGNED_URL } from '../../../graph-queries/tar/media';
import { useNoticeBar } from '../../molecules/NoticeBar/NoticeBar';
import { CertificateStatusEnum, CertificateTypeEnum, MediaContext, MediaMetadataMimeEnum } from '../../../graph-queries/__generated__/graphql';
import { MEDIA_ALLOWED_MIME_TYPES } from '../../../lib/constants/dbConstants';
import axios from 'axios';
import stepClasses from './JointSigningStep.module.scss';
import { useMsal } from '@azure/msal-react';

const MAX_FILE_SIZE_ALLOWED = 100_000_000;

const JointSigningForm: FC<{
  joint: TJoint;
  step: TJointInstallationStep;
  onClose: () => void;
  certificateStatus: CertificateStatusEnum;
  onStepSigned?: () => void;
}> = ({ joint, step, onClose, certificateStatus, onStepSigned }) => {
  const { sendSuccess, sendError } = useNoticeBar();
  const [isSignSuccess, setSignSuccess] = useState(false);
  const [comments, setComments] = useState(step.comments || '');
  const [hasFlangeDefect, setHasFlangeDefect] = useState(joint.hasFlangeDefect);
  const [actualTorque, setActualTorque] = useState(joint.actualTorque || '');
  const [state, dispatch] = useMediaUploadReducer();
  const [generateUrl] = useMutation(GENERATE_SIGNED_URL);

  const [updateJoint, { loading: isUpdating }] = useMutation(UPDATE_JOINT, {
    refetchQueries: [{ query: GET_JOINT_DETAILS, variables: { jointId: joint.jointId } }],
    awaitRefetchQueries: true,
  });

  const [updateJointStep, { loading: isUpdatingStep }] = useMutation(UPDATE_JOINT_STEP, {
    refetchQueries: [{ query: GET_JOINT_DETAILS, variables: { jointId: joint.jointId } }],
    awaitRefetchQueries: true,
  });

  const { data: jointAssembledDetails, loading: isJointAssembledDetailsLoading } = useQuery(
    GET_JOINT_INSTALLATION_STEP_DETAILS,
    {
      variables: { jointId: joint.jointId, stepNo: 2 },
    }
  );

  const { data: jointTightenedDetails, loading: isJointTightenedDetailsLoading } = useQuery(
    GET_JOINT_INSTALLATION_STEP_DETAILS,
    {
      variables: { jointId: joint.jointId, stepNo: 3 },
    }
  );
  const { data: jointWitnessedDetails, loading: isJointWitnessedDetailsLoading } = useQuery(
    GET_JOINT_INSTALLATION_STEP_DETAILS,
    {
      variables: { jointId: joint.jointId, stepNo: 4 },
    }
  );
  
  const [signJointStep, { loading: isSigning }] = useMutation(SIGN_JOINT_STEP, {
    refetchQueries: [
      { query: GET_JOINT_DETAILS, variables: { jointId: joint.jointId } },
      { 
        query: GET_JOINT_INSTALLATION_STEP_DETAILS, 
        variables: { jointId: joint.jointId, stepNo: 2 } 
      },
      { 
        query: GET_JOINT_INSTALLATION_STEP_DETAILS, 
        variables: { jointId: joint.jointId, stepNo: 3 } 
      },
      { 
        query: GET_JOINT_INSTALLATION_STEP_DETAILS, 
        variables: { jointId: joint.jointId, stepNo: 4 } 
      },
    ],
    awaitRefetchQueries: true,
  });

  const { accounts } = useMsal();
  const currentUser = accounts[0];
  const currentUserName = currentUser?.name;
  const currentUserEmail = currentUser?.username;

  const hasNewJointChanges =
    state.fileRows.length ||
    hasFlangeDefect !== joint.hasFlangeDefect ||
    actualTorque !== joint.actualTorque;

  const hasNewStepChanges = comments !== step.comments;

  const hasNewChanges = hasNewJointChanges || hasNewStepChanges;


  const isCertificateComplete = certificateStatus === CertificateStatusEnum.Complete;

  const handleUploadSuccess = (generated: { blobLocation: string; url: string }, file: File) => {
    return updateJoint({
      variables: {
        input: {
          jointId: joint.jointId,
          hasFlangeDefect,
          actualTorque,
          certificateId: joint.certificateId,
          jointNumber: joint.jointNumber,
          defectFileName: file.name,
          defectBlobLocation: generated.blobLocation,
          defectMime: file.type,
        },
      },
    });
  };

  const onSaveClick = async () => {
    if (hasNewJointChanges) {
      if (state.fileRows.length) {
        await Promise.all(state.fileRows.map((row) => uploadFile(row.file, handleUploadSuccess)))
          .then(() => {
            sendSuccess(
              `Your ${state.fileRows.length === 1 ? 'file has' : 'files have'} been uploaded successfully!`
            );
            dispatch({ type: 'reset' });
          })
          .catch(() => sendError('Upload failed. Remove invalid file to continue.'));
      } else {
        await updateJoint({
          variables: {
            input: {
              jointId: joint.jointId,
              hasFlangeDefect,
              actualTorque,
              certificateId: joint.certificateId,
              jointNumber: joint.jointNumber,
            },
          },
        });
      }
    }

    // If step fields are changed (e.g. comments)
    if (hasNewStepChanges) {
      await updateJointStep({ variables: { stepId: step.stepId, comments } });
    }
  };

  const validateUserCanSignStep = (): {
    isSignedBySamePerson: boolean;
    message?: string;
  } => {
    const isSbt = joint.tarCertificate?.type === CertificateTypeEnum.Sbt;
    
    // For SBT certificates: validate step 2 (Assembled) and step 4 (Witnessed)
    if (isSbt && (step.stepNo === 2 || step.stepNo === 4)) {
      if (step.stepNo === 4) {
        // Current step is Witnessed, check if user signed Assembled
        const jointAssembledStep = jointAssembledDetails?.jointInstallationStepDetails;
        if (jointAssembledStep?.signedBy) {
          const assembledStepSignedBy: string = jointAssembledStep.signedBy;
          if (assembledStepSignedBy === `[${currentUserName}](${currentUserEmail})`) {
            return {
              isSignedBySamePerson: true,
              message:
                'You cannot sign this step as you have signed the assembled step. Please contact your administrator for further assistance.',
            };
          }
        }
      } else {
        // Current step is Assembled, check if user signed Witnessed
        const jointWitnessedSbtStep = jointWitnessedDetails?.jointInstallationStepDetails;
        if (jointWitnessedSbtStep?.signedBy) {
          const witnessedStepSignedBy: string = jointWitnessedSbtStep.signedBy;
          if (witnessedStepSignedBy === `[${currentUserName}](${currentUserEmail})`) {
            return {
              isSignedBySamePerson: true,
              message:
                'You cannot sign this step as you have signed the witness step. Please contact your administrator for further assistance.',
            };
          }
        }
      }
    }
    
    // For non-SBT certificates: validate step 3 (Tightened) and step 4 (Witnessed)
    if (!isSbt && (step.stepNo === 4 || step.stepNo === 3)) {
      if (step.stepNo === 4) {
        // Current step is Witnessed, check if user signed Tightened
        const jointTightenedStep = jointTightenedDetails?.jointInstallationStepDetails;
        if (jointTightenedStep?.signedBy) {
          const tightenedStepSignedBy: string = jointTightenedStep.signedBy;
          if (tightenedStepSignedBy === `[${currentUserName}](${currentUserEmail})`) {
            return {
              isSignedBySamePerson: true,
              message:
                'You cannot sign this step as you have signed the tightening step. Please contact your administrator for further assistance.',
            };
          }
        }
      } else {
        // Current step is Tightened, check if user signed Witnessed
        const jointWitnessedStep = jointWitnessedDetails?.jointInstallationStepDetails;
        if (jointWitnessedStep?.signedBy) {
          const witnessedStepSignedBy: string = jointWitnessedStep.signedBy;
          if (witnessedStepSignedBy === `[${currentUserName}](${currentUserEmail})`) {
            return {
              isSignedBySamePerson: true,
              message:
                'You cannot sign this step as you have signed the witness step. Please contact your administrator for further assistance.',
            };
          }
        }
      }
    }
    return { isSignedBySamePerson: false };
  };

  // Re-evaluate `validateUserCanSignStep` whenever step number, joint details or user info changes
  const validateUserCanSignStepResult = useMemo(() => validateUserCanSignStep(), [
    step.stepNo, 
    jointTightenedDetails, 
    jointWitnessedDetails,
    jointAssembledDetails,
    joint.tarCertificate?.type,
    currentUserName, 
    currentUserEmail
  ]);

  const getAddSignatureButtonDisabledTitle = (): string | undefined => {
    if (isCertificateComplete) {
      return 'Cannot add signature - certificate is already complete';
    }

    if (
      !isCertificateComplete &&
      validateUserCanSignStepResult.isSignedBySamePerson
    ) {
      return validateUserCanSignStepResult.message;
    }

    return undefined; // No title when button is enabled
  };

  const onSubmitClick = async () => {
    try {
      if (hasNewChanges) {
        await onSaveClick();
      }
      await signJointStep({ variables: { stepId: step.stepId, comments: comments } });
  
      // Calling callback after successful joint signing
      if (onStepSigned) {
        onStepSigned();
      }
      setSignSuccess(true);
    } catch (error) {
      console.error('Error signing joint step:', error);
    }
  };

  // Note: Reused from MediaUpload.tsx; can be cleaned up to avoid redundancy
  const uploadFile = (
    file: File,
    handleUploadSuccess: (
      generated: { blobLocation: string; url: string },
      file: File
    ) => Promise<unknown>
  ) => {
    if (file.size > MAX_FILE_SIZE_ALLOWED) {
      const error = Error('File size exceeds 100MB');
      dispatch({ type: 'fail', file, error });
      return Promise.reject(error);
    }

    const mime = Object.entries(MEDIA_ALLOWED_MIME_TYPES).find(([key, val]) => {
      if (val === file.type) return key;
    })?.[0] as MediaMetadataMimeEnum;
    if (!mime) {
      const error = Error('Invalid file type');
      dispatch({ type: 'fail', file, error });
      return Promise.reject(error);
    }

    return generateUrl({
      variables: { fileName: file.name, mime: file.type, mediaContext: MediaContext.Joint },
    })
      .then((res) => {
        if (!res.data) return Promise.reject('Upload URL not generated');

        const generated = res.data.generateSignedUrl;

        return axios
          .put(generated.url, file, {
            headers: { 'x-ms-blob-type': 'BlockBlob' },
            onUploadProgress: (progressEvent) => {
              dispatch({ type: 'progress', file, progress: progressEvent.progress || 0 });
            },
          })
          .then(() => generated);
      })
      .then((generated) => handleUploadSuccess(generated, file))
      .then(() => {
        dispatch({ type: 'complete', file });
      })
      .catch((error) => {
        console.error(error);
        dispatch({ type: 'fail', file, error });
        throw error;
      });
  };

  const retry = (file: File) => {
    dispatch({ type: 'start', file });
    uploadFile(file, handleUploadSuccess).catch(() =>
      sendError('Upload failed. Remove invalid file to continue.')
    );
  };

  return isSignSuccess ? (
    <SignatureSuccess step={step} />
  ) : (
    <Form className="row g-3 px-5">
      {/* Joint Fields */}
      {step.stepNo === 1 && (
        <div className="col-md-12">
          <FormGroup onChange={() => setHasFlangeDefect((val) => !val)}>
            <Label for="has-flange-defect">Defect Identified</Label>
            <div className="d-flex">
              <FormGroup check inline className="me-5">
                <Input
                  id="has-flange-defect-1"
                  name="hasFlangeDefect"
                  type="radio"
                  defaultChecked={hasFlangeDefect === true}
                />
                <Label check for="has-flange-defect-1" className="ms-1">
                  Yes
                </Label>
              </FormGroup>
              <FormGroup check inline className="me-0">
                <Input
                  id="has-flange-defect-2"
                  name="hasFlangeDefect"
                  type="radio"
                  defaultChecked={hasFlangeDefect === false}
                />
                <Label check for="has-flange-defect-2" className="ms-1">
                  No
                </Label>
              </FormGroup>
            </div>
          </FormGroup>
        </div>
      )}

      {step.stepNo === 3 && (
        <div className="col-md-12">
          <FormGroup>
            <Label for="actual-torque">Actual torque value and units</Label>
            <Input
              value={actualTorque}
              onChange={(e) => setActualTorque((e.target as HTMLInputElement).value)}
              placeholder="Add actual torque value and units"
              type="text"
              name="actualTorque"
              id="actual-torque"
            />
          </FormGroup>
        </div>
      )}

      <div className="col-md-12">
        <FormGroup>
          <Input
            value={comments}
            onChange={(e) => setComments((e.target as HTMLTextAreaElement).value)}
            placeholder="Add comments"
            type="textarea"
            name="planRef"
            id="step-comments"
          />
        </FormGroup>
      </div>

      {step.stepNo === 1 &&
        hasFlangeDefect &&
        (state.fileRows.length ? (
          <div className="col-md-12">
            <div>
              {state.fileRows.map((row) => (
                <MediaUploadRow key={row.file.name} row={row} dispatch={dispatch} onRetry={retry} />
              ))}
            </div>
          </div>
        ) : (
          <>
            {joint?.defectMediaUrl && (
              <div className="col-md-12">
                <img
                  className={classes.previewImg}
                  src={joint.defectMediaUrl}
                  alt="Joint defect identified"
                ></img>
              </div>
            )}
            <div className="col-md-4">
              <FormGroup className={classes.uploadBtn}>
                <Label for="flange-defect-file">
                  <Camera24 /> {joint?.defectMediaUrl ? 'Update' : 'Add image'}
                </Label>
                <Input
                  id="flange-defect-file"
                  type="file"
                  placeholder="Add file"
                  onChange={(e) => {
                    dispatch({
                      type: 'init',
                      fileList: (e.target as HTMLInputElement)?.files || [],
                    });
                  }}
                />
              </FormGroup>
            </div>
          </>
        ))}

      {/* Footer */}
      <div className="col-md-12 mt-6 d-flex justify-content-between align-items-center">
        <div>
          {step.signedBy && (
            <>
              <div className={stepClasses.signedBy}>{extractAndFormatName(step.signedBy)}</div>
              <CircleCheck24
                className={stepClasses.checkIcon}
                title={`Signed by ${extractAndFormatName(step.signedBy)}`}
              />
            </>
          )}
        </div>
        <div className="d-flex gap-4 flex-row-reverse">
          <Button
            size="lg"
            className=""
            rounded="0"
            level="primary"
            disabled={
              isUpdating ||
              isUpdatingStep ||
              isSigning ||
              isCertificateComplete ||
              validateUserCanSignStepResult.isSignedBySamePerson ||
              isJointWitnessedDetailsLoading ||
              isJointTightenedDetailsLoading
            }
            onClick={onSubmitClick}
            title={getAddSignatureButtonDisabledTitle()}
          >
            Add Signature
          </Button>
          {hasNewChanges && (
            <Button
              size="lg"
              className=""
              level="secondary"
              onClick={onSaveClick}
              disabled={
                isUpdating ||
                isUpdatingStep ||
                isCertificateComplete ||
                validateUserCanSignStepResult.isSignedBySamePerson ||
                isJointWitnessedDetailsLoading ||
                isJointTightenedDetailsLoading
              }
              title={getAddSignatureButtonDisabledTitle()}
            >
              Save
            </Button>
          )}
        </div>
      </div>
    </Form>
  );
};

const SignatureSuccess: FC<{
  step: TJointInstallationStep;
}> = ({ step }) => {
  if (!step.signedBy) return null;
  return (
    <div className={classes.signatureSuccess}>
      <div className={classes.msg}>
        <div>
          <CircleCheck64 />
        </div>
        <div>Confirmed</div>
      </div>
      <div className={classes.details}>
        <div>
          <div>Signed</div>
          <div className={classes.value}>{extractAndFormatName(step.signedBy)}</div>
        </div>
        <div>
          <div>Date</div>
          <div className={classes.value}>{formatDate(step.signedAt, 'dd-MMM-yyyy')}</div>
        </div>
        <div>
          <div>Time</div>
          <div className={classes.value}>{formatDate(step.signedAt, 'HH:mm:SS')}</div>
        </div>
      </div>
    </div>
  );
};

export default JointSigningForm;
